// ═══════════════════════════════════════════════════════════════════════════════
// FOLLOW-UP EMAIL TEMPLATES v1.0
// EnergiPro Solutions — Professional Follow-Up Content
// ═══════════════════════════════════════════════════════════════════════════════
// Tone: Professional, contractor-grade, no generic wording
// Each template references: RFQ ID, Service Name, Client Name
// Sender: rfq@energiprogroup.com
// ═══════════════════════════════════════════════════════════════════════════════

import { FollowUpTrackingRecord, SENDER_EMAIL } from './followUpEngine';
import { generateEmailContactBlock } from './multiChannelContactEngine';

export interface FollowUpEmailContent {
  subject: string;
  greeting: string;
  body: string[];
  callToAction: string;
  closing: string;
  signature: string;
}

// ─── TEMPLATE GENERATOR ──────────────────────────────────────────────────────

export function generateFollowUpEmail(
  record: FollowUpTrackingRecord,
  stage: 1 | 2 | 3 | 4
): FollowUpEmailContent {
  switch (stage) {
    case 1:
      return generateStage1(record);
    case 2:
      return generateStage2(record);
    case 3:
      return generateStage3(record);
    case 4:
      return generateStage4(record);
  }
}

// ─── STAGE 1: CONFIRMATION (Day 1) ──────────────────────────────────────────

function generateStage1(record: FollowUpTrackingRecord): FollowUpEmailContent {
  return {
    subject: `[${record.referenceId}] Technical Proposal — Confirmation of Receipt`,
    greeting: `Dear ${record.clientName},`,
    body: [
      `Just confirming — our technical proposal for ${record.serviceName} has been submitted to your team under reference ${record.referenceId}.`,
      `We've prioritized your request in our engineering workflow, so you're not waiting in a queue.`,
      `The proposal covers equipment specs, mobilization timeline, and preliminary commercial structure. We selected this configuration based on what's worked well in similar field conditions.`,
      `━━━ EXECUTION READINESS ━━━`,
      `• Equipment Availability: Confirmed`,
      `• Engineering Review: Completed`,
      `• Mobilization Readiness: Immediate upon approval`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `If you need any spec sheets, technical clarification, or supporting docs for your internal review — just let us know. We can turn those around quickly.`,
      `You can also track your RFQ status anytime through our Client Portal at portal.energiprogroup.com — real-time updates on progress, documents, and next steps.`,
      generateEmailContactBlock('high', record.referenceId, record.serviceName),
    ],
    callToAction: 'Drop us a quick reply to confirm receipt, or let us know if there\'s anything else you need for your review.',
    closing: 'Talk soon,',
    signature: getSignature(),
  };
}

// ─── STAGE 2: ENGAGEMENT (Day 3) ────────────────────────────────────────────

function generateStage2(record: FollowUpTrackingRecord): FollowUpEmailContent {
  return {
    subject: `[${record.referenceId}] Technical Clarification Available — ${record.serviceName}`,
    greeting: `Dear ${record.clientName},`,
    body: [
      `Quick follow-up on our ${record.serviceName} proposal (Ref: ${record.referenceId}). Wanted to share a few points that might help your evaluation:`,
      `• Equipment Configuration — The package we've proposed is pre-qualified for your operating conditions. We selected it based on what's delivered results in similar jobs. Data sheets and P&IDs are ready if you need them.`,
      `• Mobilization Readiness — Everything is staged, inspected, and good to go within the timeline we quoted. No outstanding dependencies on our end.`,
      `• Technical Discussion — Our lead engineer is happy to jump on a quick call to address any spec questions, discuss alternatives, or adjust scope based on your team's feedback.`,
      `━━━ EXECUTION READINESS ━━━`,
      `• Equipment Availability: Confirmed`,
      `• Engineering Review: Completed`,
      `• Mobilization Readiness: Immediate upon approval`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `We're ready to move forward whenever you are. Just say the word.`,
      `Your RFQ status is always visible through our Client Portal — real-time progress tracking, documents, and next steps all in one place.`,
      generateEmailContactBlock('high', record.referenceId, record.serviceName),
    ],
    callToAction: 'Would a quick technical call be helpful? Let us know a time that works and we\'ll set it up.',
    closing: 'Best,',
    signature: getSignature(),
  };
}

// ─── STAGE 3: URGENCY (Day 7) ───────────────────────────────────────────────

function generateStage3(record: FollowUpTrackingRecord): FollowUpEmailContent {
  return {
    subject: `[${record.referenceId}] Equipment Availability Update — Action Required`,
    greeting: `Dear ${record.clientName},`,
    body: [
      `Wanted to give you a heads-up on equipment availability for your ${record.serviceName} requirement (Ref: ${record.referenceId}), which we submitted ${formatProposalAge(record)} ago.`,
      `The specific configuration we proposed — including pressure-rated components, safety systems, and support equipment — is still allocated for your project. Our fleet stays busy, so we're holding this specifically for you.`,
      `━━━ EXECUTION READINESS (CONFIRMED) ━━━`,
      `• Equipment Availability: Confirmed & Reserved`,
      `• Engineering Review: Completed — configuration locked`,
      `• Mobilization Readiness: Immediate upon approval`,
      `• Configuration: Field-proven under similar operating conditions`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `To keep this allocation active, it would help to know your decision timeline or if there's anything holding things up on your end.`,
      `If scope, budget, or technical concerns are a factor — we're open to discussing alternative configurations that might work better for you.`,
      `Track your RFQ status and take actions directly through our Client Portal — available 24/7.`,
      generateEmailContactBlock('high', record.referenceId, record.serviceName),
    ],
    callToAction: 'Could you let us know your expected timeline? A quick email or call is all we need to keep things reserved for you.',
    closing: 'Best regards,',
    signature: getSignature(),
  };
}

// ─── STAGE 4: FINAL FOLLOW-UP (Day 14) ──────────────────────────────────────

function generateStage4(record: FollowUpTrackingRecord): FollowUpEmailContent {
  return {
    subject: `[${record.referenceId}] Final Follow-Up — Proposal Status`,
    greeting: `Dear ${record.clientName},`,
    body: [
      `This is our final follow-up on the ${record.serviceName} proposal submitted under reference ${record.referenceId}.`,
      `It's been ${formatProposalAge(record)} since we sent this over, and we haven't heard back on where things stand. We typically close out inactive proposals after 14 days so we can reallocate equipment and resources — but we wanted to check in one more time before doing that.`,
      `Here's what would help us on our end:`,
      `  1. Green light to proceed — We'll issue updated commercial terms and lock your mobilization dates.`,
      `  2. Still evaluating — Just let us know your expected timeline and we'll keep things reserved.`,
      `  3. No longer needed — No problem at all. A quick confirmation helps us keep our records clean.`,
      `This way we can make sure your project keeps its priority allocation if things do move forward.`,
      generateEmailContactBlock('high', record.referenceId, record.serviceName),
    ],
    callToAction: 'A quick reply with your status (proceed / pending / cancel) would be much appreciated.',
    closing: 'Thanks,',
    signature: getSignature(),
  };
}

// ─── SIGNATURE ───────────────────────────────────────────────────────────────

function getSignature(): string {
  return `EnergiPro Solutions
Commercial & Technical Coordination
Email: ${SENDER_EMAIL}
Al Khobar, Eastern Province, KSA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CONFIDENTIALITY NOTICE: This email and any attachments are intended solely for the addressee. 
If you are not the intended recipient, please notify the sender immediately and delete this message.`;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatProposalAge(record: FollowUpTrackingRecord): string {
  const diff = Date.now() - new Date(record.proposalSentDate).getTime();
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days === 1) return '1 day';
  return `${days} days`;
}

/**
 * Generate full email as plain text for clipboard/preview
 */
export function renderFollowUpAsText(email: FollowUpEmailContent): string {
  return [
    `From: ${SENDER_EMAIL}`,
    `Subject: ${email.subject}`,
    '',
    email.greeting,
    '',
    ...email.body.map(p => p),
    '',
    email.callToAction,
    '',
    email.closing,
    '',
    email.signature,
  ].join('\n');
}

/**
 * Generate full email as HTML for preview
 */
export function renderFollowUpAsHTML(email: FollowUpEmailContent): string {
  const bodyHtml = email.body
    .map(p => {
      if (p.startsWith('  ') || p.startsWith('•')) {
        return `<p style="margin:6px 0 6px 20px;color:#334155;">${p}</p>`;
      }
      return `<p style="margin:12px 0;color:#1e293b;line-height:1.6;">${p}</p>`;
    })
    .join('');

  return `
<div style="font-family:'Segoe UI',Arial,sans-serif;max-width:680px;margin:0 auto;padding:32px;background:#ffffff;border:1px solid #e2e8f0;border-radius:8px;">
  <div style="border-bottom:3px solid #ea580c;padding-bottom:16px;margin-bottom:24px;">
    <h3 style="margin:0;color:#0f172a;font-size:14px;letter-spacing:0.5px;">ENERGIPRO SOLUTIONS</h3>
    <p style="margin:4px 0 0;color:#64748b;font-size:12px;">Technical & Commercial Coordination</p>
  </div>
  
  <p style="color:#475569;font-size:12px;margin-bottom:8px;"><strong>Subject:</strong> ${email.subject}</p>
  <p style="color:#475569;font-size:12px;margin-bottom:20px;"><strong>From:</strong> ${SENDER_EMAIL}</p>
  
  <p style="color:#1e293b;font-weight:500;margin-bottom:16px;">${email.greeting}</p>
  
  ${bodyHtml}
  
  <div style="margin:24px 0;padding:16px;background:#fff7ed;border-left:4px solid #ea580c;border-radius:4px;">
    <p style="margin:0;color:#9a3412;font-weight:500;font-size:14px;">${email.callToAction}</p>
  </div>
  
  <p style="color:#475569;margin-top:24px;">${email.closing}</p>
  
  <div style="margin-top:24px;padding-top:16px;border-top:1px solid #e2e8f0;">
    <pre style="font-family:'Segoe UI',Arial,sans-serif;font-size:12px;color:#64748b;white-space:pre-wrap;margin:0;">${email.signature}</pre>
  </div>
</div>`;
}
